import { publishFailureForMethod } from '@amzn/ts-ui-metrics';
import { cloudwatchMetricsPublisher } from './metricsPublisher';

/**
 * Normalize API paths to prevent high-cardinality metric dimensions.
 * Strips query params and replaces complete numeric path segments with {id}.
 * Only matches segments that are purely digits (anchored to / boundary or end).
 */
export function normalizeMethodName(path) {
  return path.split('?')[0].replace(/\/\d+(?=\/|$)/g, '/{id}');
}

/**
 * Publish per-request latency. CloudWatch SampleCount on this metric
 * gives total call count, so no separate Count metric is needed.
 */
export function publishRequestMetrics(requestPath, durationMs) {
  try {
    const methodName = normalizeMethodName(requestPath);
    const publisher = cloudwatchMetricsPublisher.newChildActionPublisherForMethod(methodName);
    publisher.publishTimerMonitor('Latency', durationMs);
  } catch (e) {
    console.warn('[publishRequestMetrics] failed for', requestPath, e);
  }
}

/**
 * Status ApiClient assigns when fetch() rejects, i.e. the browser never handed
 * us an HTTP response at all. It is not a real HTTP status.
 */
export const FETCH_REJECTED_STATUS = 520;

const BUNDLING_EXPANSION_METHOD_NAME = 'OfferBundlingExpansionState';

/** The states the offerBundlingExpansion flag can resolve to for a linguist. */
export const BUNDLING_EXPANSION_STATES = Object.freeze({
  ON: 'On',
  OFF: 'Off',
  UNAVAILABLE: 'Unavailable',
});

/**
 * Publish which offerBundlingExpansion value a linguist resolved to, so the dial-up is observable
 * and a flag that reaches nobody is distinguishable from one that is deliberately off. Call once
 * per offers-view fetch; the treated share is On / (On + Off + Unavailable).
 *
 * `Unavailable` covers an absent or non-boolean value. Those bundle with the base list exactly as
 * `Off` does, but they mean the flag never arrived rather than that it resolved to false -- and
 * since every failure in that chain lands on false, nothing else distinguishes the two.
 *
 * Each state is its own counter rather than one metric dimensioned by state: CloudWatch has no
 * string metrics, and dimensions on this path come from the metrics context rather than from
 * publishString, so the state has to ride the metric name. Deriving the state here rather than
 * accepting one keeps the metric namespace bounded to these three.
 *
 * @param {*} rawFlagValue the `offerBundlingExpansion` field exactly as the response carried it
 */
export function publishBundlingExpansionState(rawFlagValue) {
  try {
    let state = BUNDLING_EXPANSION_STATES.UNAVAILABLE;
    if (typeof rawFlagValue === 'boolean') {
      state = rawFlagValue ? BUNDLING_EXPANSION_STATES.ON : BUNDLING_EXPANSION_STATES.OFF;
    }
    const publisher = cloudwatchMetricsPublisher.newChildActionPublisherForMethod(
      BUNDLING_EXPANSION_METHOD_NAME
    );
    publisher.publishCounter(state, 1);
  } catch (e) {
    console.warn('[publishBundlingExpansionState] failed for', rawFlagValue, e);
  }
}

const OFFER_BUNDLE_CLAIM_METHOD_NAME = 'OfferBundleClaim';

/** The two counters one bundle claim resolves into: member offers that updated vs that failed. */
export const BUNDLE_CLAIM_OUTCOMES = Object.freeze({
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
});

/**
 * Publish the outcome of one bundle claim so the claim-failure rate is alarmable in CloudWatch,
 * which the Sushi-backed per-offer counters never reach. Call once per bundle claim with the
 * counts the fan-out already computed: offers whose status update succeeded and offers that
 * failed, which together sum to the bundle's member count.
 *
 * This measures a bundle claim as a whole, not an individual offer. A bundle claim fans out one
 * status-update request per member offer, so a bundle is the only claim that can partially fail;
 * a single-offer claim wholly succeeds or wholly fails and is already covered in prod by
 * OfferStatusUpdateErrorAlarm. Both counters are emitted every time, including a zero, so the
 * Failed-over-total rate stays well-defined when a claim fully succeeds or fully fails.
 *
 * @param {number} succeededCount member offers whose status update succeeded
 * @param {number} failedCount member offers whose status update failed
 */
export function publishBundleClaimOutcome(succeededCount, failedCount) {
  try {
    const publisher = cloudwatchMetricsPublisher.newChildActionPublisherForMethod(
      OFFER_BUNDLE_CLAIM_METHOD_NAME
    );
    publisher.publishCounter(BUNDLE_CLAIM_OUTCOMES.SUCCEEDED, succeededCount);
    publisher.publishCounter(BUNDLE_CLAIM_OUTCOMES.FAILED, failedCount);
  } catch (e) {
    console.warn('[publishBundleClaimOutcome] failed', succeededCount, failedCount, e);
  }
}

const OFFER_BUNDLING_SHOWN_METHOD_NAME = 'OfferBundlingShown';

/**
 * Publish how one Offers page load split its offers between bundle rows and single rows, so the
 * bundled share and average bundle size can be charted in CloudWatch. Call once per bundling pass.
 * The counter names end in Expanded or Base for the allowlist in effect, because dimensions on
 * this path come only from the metrics context; zeros are published so every page load counts.
 */
export function publishBundlingShown(expansionEnabled, bundledOffers, singleOffers, bundles) {
  try {
    const list = expansionEnabled ? 'Expanded' : 'Base';
    const publisher = cloudwatchMetricsPublisher.newChildActionPublisherForMethod(
      OFFER_BUNDLING_SHOWN_METHOD_NAME
    );
    publisher.publishCounter(`BundledOffers${list}`, bundledOffers);
    publisher.publishCounter(`SingleOffers${list}`, singleOffers);
    publisher.publishCounter(`Bundles${list}`, bundles);
  } catch (e) {
    console.warn('[publishBundlingShown] failed', expansionEnabled, e);
  }
}

export async function publishRequestFailureMetric(requestPath, status) {
  try {
    const methodName = normalizeMethodName(requestPath);
    if (status === FETCH_REJECTED_STATUS) {
      // No readable response: CORS/preflight refusal, aborted navigation,
      // extension block, or genuine connectivity loss. The browser hides which,
      // so this cannot be attributed client-side and is NOT a backend 5xx.
      publishFailureForMethod(cloudwatchMetricsPublisher, methodName, 'FetchRejected');
    } else if (status >= 500) {
      publishFailureForMethod(cloudwatchMetricsPublisher, methodName, '5xx');
    }
  } catch (e) {
    console.warn('[publishRequestFailureMetric] failed for', requestPath, status, e);
  }
}
